/**
 * CarPaint Preset — High-quality automotive lacquer material.
 *
 * Uses Three.js FlakesTexture as normalMap for metallic flake simulation.
 * The FlakesTexture stays active in ALL finish types — only normalScale
 * and PBR values vary. This avoids texture swaps mid-transition
 * which would cause visual artifacts.
 *
 * Preset values derived from real-world automotive paint measurements.
 */

import * as THREE from 'three';
import { FlakesTexture } from 'three/addons/textures/FlakesTexture.js';
import type { CarPaintType } from '../../api/types';
import { devWarn } from '../../utils/devLog';

export type { CarPaintType };

/** PBR + normalScale values for a single finish type. */
export interface CarPaintValues {
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
  /** normalScale controls perceived flake intensity without swapping the map. */
  normalScale: number;
}

// ---------------------------------------------------------------------------
// Preset Definitions
// ---------------------------------------------------------------------------

/**
 * Validated preset values for each finish type.
 * normalScale: metallic (strong flake), uni (subtle shimmer), matte (soft flake)
 */
export const CAR_PAINT_PRESETS: Record<CarPaintType, CarPaintValues> = {
  metallic: {
    metalness: 0.8,
    roughness: 0.4,
    clearcoat: 1.0,
    clearcoatRoughness: 0.02,
    normalScale: 0.15,
  },
  uni: {
    metalness: 0.4,
    roughness: 0.4,
    clearcoat: 1.0,
    clearcoatRoughness: 0.02,
    normalScale: 0.01,
  },
  matte: {
    metalness: 0.8,
    roughness: 0.4,
    clearcoat: 1.0,
    clearcoatRoughness: 0.3,
    normalScale: 0.15,
  },
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a new CarPaint MeshPhysicalMaterial.
 *
 * @param colorCode - HEX color string (e.g. '#3a3a3a')
 * @param type      - Finish type (default: 'metallic')
 */
export function createCarPaintMaterial(
  colorCode: string,
  type: CarPaintType | string = 'metallic',
  opts?: { doubleSide?: boolean },
): THREE.MeshPhysicalMaterial {
  const preset = CAR_PAINT_PRESETS[type as CarPaintType] ?? (() => {
    devWarn('createCarPaintMaterial()', `unknown finish type "${type}" — falling back to "uni".`, {
      'Received type': type,
      'Valid types':   Object.keys(CAR_PAINT_PRESETS),
    });
    return CAR_PAINT_PRESETS.uni;
  })();

  // 1024×1024 reduces the tiling grid artefact visible at grazing angles.
  // Irrational repeat values (not multiples of 0.5) further break up the seam pattern.
  const flakesCanvas = new FlakesTexture(1024, 1024);
  const normalMap = new THREE.CanvasTexture(flakesCanvas);
  normalMap.wrapS = THREE.RepeatWrapping;
  normalMap.wrapT = THREE.RepeatWrapping;
  normalMap.repeat.set(1, 1);
  normalMap.anisotropy = 16;

  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(colorCode),
    metalness: preset.metalness,
    roughness: preset.roughness,
    clearcoat: preset.clearcoat,
    clearcoatRoughness: preset.clearcoatRoughness,
    normalMap,
    normalScale: new THREE.Vector2(preset.normalScale, preset.normalScale),
    side: opts?.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
  });

  return mat;
}

/**
 * Return the preset values for a given finish type.
 * Useful for lerp targets without creating a full material.
 *
 * Falls back to 'uni' for unknown/unsupported type strings so callers
 * never receive an empty object (which causes a black material).
 */
export function getCarPaintPreset(type: CarPaintType | string): CarPaintValues {
  const preset = CAR_PAINT_PRESETS[type as CarPaintType];
  if (!preset) {
    devWarn('getCarPaintPreset()', `unknown finish type "${type}" — falling back to "uni".`, {
      'Received type': type,
      'Valid types':   Object.keys(CAR_PAINT_PRESETS),
      'Hint':          'Check your colors.json or setMaterial() call. Valid types are: metallic, uni, matte.',
    });
    return { ...CAR_PAINT_PRESETS.uni };
  }
  return { ...preset };
}
